import { FC, useContext, useEffect, useMemo } from 'react';
import {
  HandleProps,
  useConnection as useActiveConnectionInteraction,
  useNodeConnections,
  useUpdateNodeInternals,
  type HandleType,
} from '@xyflow/react';
import type { UUIDType } from '../../../../../common/utils';
import { GraphHandle } from '../../../GraphHandle';
import { getHandleId } from '../../utils';
import { AlgorithmJoinsWithMappingsGraphContext } from '../AlgorithmJoinsWithMappingsGraphContext';

export interface AlgorithmJoinsAndMappingsGraphHandleProps extends HandleProps {
  nodeId: UUIDType;
  itemIndex?: number;
  multipleConnections?: boolean;
}

export const AlgorithmJoinsAndMappingsGraphHandle: FC<
  AlgorithmJoinsAndMappingsGraphHandleProps
> = (props) => {
  const {
    nodeId,
    itemIndex = 0,
    multipleConnections = false,
    type,
    ...rest
  } = props;
  const handleId = getHandleId(nodeId, itemIndex, type);
  const { hasOwnConnections, hasOppositeConnections } = useHasConnections(
    nodeId,
    handleId,
    type,
  );
  const { readOnly } = useContext(AlgorithmJoinsWithMappingsGraphContext);
  const isHidden =
    (readOnly && !hasOwnConnections) ||
    (!multipleConnections && hasOppositeConnections);

  useUpdateEdgesOnShow(nodeId, isHidden);

  const isInvalidConnectionReceiver = useIsInvalidConnectionReceiver(handleId);

  if (isHidden) {
    return null;
  }

  return (
    <GraphHandle
      id={handleId}
      type={type}
      isConnectable={multipleConnections || !hasOwnConnections}
      $isInvalid={isInvalidConnectionReceiver}
      {...rest}
    />
  );
};

const useHasConnections = (
  id: UUIDType,
  handleId: string,
  handleType: HandleType,
) => {
  const ownConnections = useNodeConnections({ id, handleId, handleType });
  const oppositeConnections = useNodeConnections({
    id,
    handleId: handleId.replace(
      handleType,
      handleType === 'source' ? 'target' : 'source',
    ),
    handleType: handleType === 'source' ? 'target' : 'source',
  });

  return {
    hasOwnConnections: ownConnections.length > 0,
    hasOppositeConnections: oppositeConnections.length > 0,
  };
};

const useUpdateEdgesOnShow = (nodeId: UUIDType, isHidden: boolean) => {
  const updateNodeInternals = useUpdateNodeInternals();
  useEffect(() => {
    if (!isHidden && nodeId) {
      updateNodeInternals(nodeId);
    }
  }, [isHidden, updateNodeInternals, nodeId]);
};

const useIsInvalidConnectionReceiver = (handleId: string): boolean => {
  const activeConnectionInteraction = useActiveConnectionInteraction();

  return useMemo(
    () =>
      activeConnectionInteraction.toHandle?.id === handleId &&
      !activeConnectionInteraction.isValid,
    [activeConnectionInteraction, handleId],
  );
};
